import { batch, useSelector } from 'react-redux'
import {
  loadingPatientDetail,
  updatePatientDetail,
} from '../redux/patient-detail-slice'
import { RootState, useAppThunkDispatch } from '../redux/store'
import { PatientService } from '../services/patient.service'
import { setMultiProgramConfirm } from '../redux/multi-program-confirmation-slice'
import Helper from './helper'
import { toast } from 'react-toastify'
import {
  setRecommendedCalculators,
  setSelectedProgram,
  setSelectedTask,
} from '../redux/common-data-slice'
import { profiler } from './profiler'

export const useUpdateDataAction = (patientId: string) => {
  const dispatch = useAppThunkDispatch()
  const patientService = new PatientService()
  const selectedTask = useSelector(
    (state: RootState) => state.commonData.selectedTask
  )
  const multiProgramConfirm = useSelector(
    (state: RootState) => state.multiProgramConfirm
  )
  const program_enrollments = useSelector(
    (state: RootState) =>
      state.patientDetail.patientDetail?.program_enrollments || []
  )
  const selectedProgram = useSelector(
    (state: RootState) => state.commonData.selectedProgram
  )
  const patientDetail = useSelector(
    (state: RootState) => state.patientDetail.patientDetail
  )

  const updatePendingCount = (pending_action_counts: any) => {
    if (Object.keys(pending_action_counts)?.length > 0) {
      const updatedCount = multiProgramConfirm.map((i: any) => {
        let currP =
          Object.keys(pending_action_counts)?.map((p: any) =>
            i.program === p ? pending_action_counts[p] : ''
          ) || []
        currP = currP?.filter((i: any) => i !== '')
        return currP && currP.length > 0 ? { ...i, pending: currP[0] } : i
      })
      dispatch(setMultiProgramConfirm(updatedCount))
    }
  }

  const updatePatientData = ({
    isMeasurement = false,
    isTreatments = false,
    isConditions = false,
    isProfileChange = false,
    flowName = '',
  }) => {
    dispatch(loadingPatientDetail('refreshing'))

    profiler.start('getPatientInfo API call')
    return patientService
      .getPatientInfo(patientId)
      .then((response: any) => {
        profiler.end('getPatientInfo API call')
        dispatch(loadingPatientDetail(''))

        const responseSize = JSON.stringify(response?.data?.patient || {}).length
        console.log(`[PROF] [INFO] Patient data response size: ${(responseSize / 1024).toFixed(1)}KB`)

        const patient = response?.data?.patient || {}

        // OPTIMIZED: Batch all patient detail updates into a single dispatch
        profiler.start('dispatch updatePatientDetail (batched)')
        const batchedUpdate: any = {
          diagnosis: patient.diagnosis,
          program_enrollments: patient.program_enrollments,
          pending_task_counts: patient.pending_task_counts,
        }

        if (isMeasurement) {
          batchedUpdate.measurements = patient.measurements
        }
        if (isTreatments) {
          batchedUpdate.medications = patient.medications
        }
        if (isConditions) {
          batchedUpdate.conditions = patient.conditions
        }
        if (isProfileChange) {
          batchedUpdate.family_name = patient.family_name || ''
          batchedUpdate.hospital_name = patient.hospital_name || ''
          batchedUpdate.given_name = patient.given_name || ''
          batchedUpdate.c_sex = patient.c_sex || ''
          batchedUpdate.email = patient.email || ''
          batchedUpdate.birthdate = patient.birthdate || ''
          batchedUpdate.phone = patient.phone || ''
          batchedUpdate.mrns = patient.mrns || []
          batchedUpdate.needs_update = patient.needs_update || false
          batchedUpdate.confirmed_ca_diagnosis = patient.confirmed_ca_diagnosis
          batchedUpdate.prior_eval_in_ca_program = patient.prior_eval_in_ca_program
          batchedUpdate.referral_pathway = patient.referral_pathway || ''
          batchedUpdate.registry_status = patient.registry_status || ''
          batchedUpdate.clinical_team = patient.clinical_team
          batchedUpdate.clinic_visit_logs = patient.clinic_visit_logs
        }

        profiler.start('batched dispatches (combined)')
        batch(() => {
          dispatch(updatePatientDetail(batchedUpdate))
          profiler.end('dispatch updatePatientDetail (batched)')

          profiler.start('updatePendingCount')
          updatePendingCount(patient.pending_action_counts)
          profiler.end('updatePendingCount')

          profiler.start('dispatch setSelectedProgram')
          const existingProgramNames = program_enrollments.map((e: any) => e.program)
          const newlySelected = (patient.program_enrollments || [])
            .filter((e: any) => !existingProgramNames.includes(e.program) && e.selected)
            .map((e: any) => e.program)
          const stillEnrolled = (patient.program_enrollments || []).map((e: any) => e.program)
          const newSelectedProgram = [
            ...selectedProgram.filter((p: string) => stillEnrolled.includes(p)),
            ...newlySelected,
          ]
          dispatch(setSelectedProgram(newSelectedProgram))
          try {
            sessionStorage.setItem(`selectedPrograms:${patientId}`, JSON.stringify(newSelectedProgram))
          } catch { /* ignore */ }
          profiler.end('dispatch setSelectedProgram')

          profiler.start('dispatch setSelectedTask')
          dispatch(setSelectedTask({ ...selectedTask, isUpdate: true }))
          profiler.end('dispatch setSelectedTask')
        })
        profiler.end('batched dispatches (combined)')

        profiler.end('updatePatientData (total)')
        if (flowName) {
          profiler.summary(flowName)
        }
      })
      .catch((err: any) => {
        profiler.end('getPatientInfo API call')
        dispatch(loadingPatientDetail(''))
        toast.error(Helper.getError(err), Helper.bottom_center())
      })
  }

  const getUpdatedObjByPrg = (
    oldData: any[] = [],
    newData: any[] = [],
    type: any
  ) => {
    return (
      newData?.map((i: any) => {
        let currCal = oldData.find((el: any) => el.id === i.id)

        if (type === 'recommended_calculators' && !currCal?.programs) {
          currCal = patientDetail?.measurements?.recommended_calculators?.find(
            (el: any) => el.id === i.id
          )
        }

        if (currCal) {
          return {
            ...i,
            programs: i?.programs || currCal?.programs || [],
            overridden_programs: currCal.overridden_programs,
          }
        } else {
          if ((i?.programs || [])?.length > 0) {
            return { ...i }
          } else {
            const prgList: string[] = []
            patientDetail?.program_enrollments?.map((e: any) => {
              if (e.program_type === 'evaluation') {
                prgList.push(e.program)
              }
            })
            return { ...i, programs: prgList }
          }
        }
      }) || []
    )
  }

  const updateDiagnosisData = async (currentProgram: any) => {
    profiler.startFlow('Evaluation Recalculate')
    try {
      profiler.start('dispatch loadingPatientDetail(dia_loading)')
      dispatch(loadingPatientDetail('dia_loading'))
      profiler.end('dispatch loadingPatientDetail(dia_loading)')

      profiler.start('recalculateDiagnosisDetails API call')
      const response = await patientService.recalculateDiagnosisDetails(
        patientId,
        currentProgram
      )
      profiler.end('recalculateDiagnosisDetails API call')

      const data = response?.data

      profiler.start('dispatch loadingPatientDetail(clear)')
      dispatch(loadingPatientDetail(''))
      profiler.end('dispatch loadingPatientDetail(clear)')

      if (!data) {
        profiler.summary('Evaluation Recalculate')
        return
      }

      const responseSize = JSON.stringify(data).length
      console.log(`[PROF] [INFO] Diagnosis data response size: ${(responseSize / 1024).toFixed(1)}KB`)

      profiler.start('parse diagnosis data')
      const {
        diagnosis_plan,
        diagnostic_predicates,
        diagnostic_tests,
        most_characteristic = [],
        measurements: {
          clinical_scores = [],
          calculators = [],
          recommended_calculators = [],
        } = {},
      } = data
      profiler.end('parse diagnosis data')

      profiler.start('getUpdatedObjByPrg - calculators')
      const latestCal = getUpdatedObjByPrg(
        patientDetail?.measurements?.calculators,
        calculators,
        'calculators'
      )
      profiler.end('getUpdatedObjByPrg - calculators')

      profiler.start('getUpdatedObjByPrg - recommended_calculators')
      const latestRecomCal = getUpdatedObjByPrg(
        patientDetail?.measurements?.recommended_calculators || [],
        recommended_calculators,
        'recommended_calculators'
      )
      profiler.end('getUpdatedObjByPrg - recommended_calculators')

      profiler.start('getUpdatedObjByPrg - diagnostic_predicates')
      const latestDiaPredicates = getUpdatedObjByPrg(
        patientDetail?.diagnosis?.diagnostic_predicates || [],
        diagnostic_predicates,
        'diagnostic_predicates'
      )
      profiler.end('getUpdatedObjByPrg - diagnostic_predicates')

      profiler.start('dispatch updatePatientDetail (diagnosis)')
      dispatch(
        updatePatientDetail({
          diagnosis: {
            ...patientDetail?.diagnosis,
            diagnosis_plan,
            diagnostic_predicates: latestDiaPredicates,
            diagnostic_tests,
            most_characteristic,
          },
          measurements: {
            ...patientDetail?.measurements,
            clinical_scores,
            calculators: latestCal,
            recommended_calculators: latestRecomCal,
          },
        })
      )
      profiler.end('dispatch updatePatientDetail (diagnosis)')

      profiler.start('dispatch setRecommendedCalculators')
      dispatch(
        setRecommendedCalculators(
          patientDetail?.measurements?.recommended_calculators || []
        )
      )
      profiler.end('dispatch setRecommendedCalculators')

      profiler.summary('Evaluation Recalculate')
    } catch (err) {
      profiler.end('recalculateDiagnosisDetails API call')
      dispatch(loadingPatientDetail(''))
      console.error('Error updating diagnosis:', err)
      toast.error(Helper.getError(err), Helper.bottom_center())
      profiler.summary('Evaluation Recalculate (error)')
    }
  }

  const updatePatientDataFromResponse = (
    patient: any,
    {
      isMeasurement = false,
      isTreatments = false,
      isConditions = false,
      flowName = '',
    }: {
      isMeasurement?: boolean
      isTreatments?: boolean
      isConditions?: boolean
      flowName?: string
    }
  ) => {
    profiler.start('dispatch updatePatientDetail (from response)')
    const batchedUpdate: any = {
      diagnosis: patient.diagnosis,
      program_enrollments: patient.program_enrollments,
      pending_task_counts: patient.pending_task_counts,
    }

    if (isMeasurement) {
      batchedUpdate.measurements = patient.measurements
    }
    if (isTreatments) {
      batchedUpdate.medications = patient.medications
    }
    if (isConditions) {
      batchedUpdate.conditions = patient.conditions
    }

    dispatch(updatePatientDetail(batchedUpdate))
    profiler.end('dispatch updatePatientDetail (from response)')

    profiler.start('updatePendingCount')
    updatePendingCount(patient.pending_action_counts)
    profiler.end('updatePendingCount')

    profiler.start('dispatch setSelectedProgram')
    dispatch(
      setSelectedProgram(
        patient.program_enrollments
          ?.filter((f: { selected: boolean }) => f.selected === true)
          .map((e: { program: any }) => e.program)
      )
    )
    profiler.end('dispatch setSelectedProgram')

    profiler.start('dispatch setSelectedTask')
    dispatch(setSelectedTask({ ...selectedTask, isUpdate: true }))
    profiler.end('dispatch setSelectedTask')

    profiler.end('updatePatientData (total)')
    if (flowName) {
      profiler.summary(flowName)
    }
  }

  return {
    updatePatientData,
    updatePatientDataFromResponse,
    updateDiagnosisData,
  }
}
